import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { db } from "../../../../firebase";
import { ref, onValue, update } from "firebase/database";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
  Grid,
  Stack,
  OutlinedInput,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { tokens } from "../../../../theme";
import SnackBar from "../../../../components/snackbar/SnackBar";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { formatterTimePicker } from "../../../../helpers/date/formatterTimePicker";
import { dateFormat, timeFormat } from "../../../../helpers/date/date";
import {
  formatDatePickerValue,
  formatTimePickerValue,
  getDateOnlyValue,
  getDateTimeValue,
} from "../booking/components/endDateTimeUtils";
import { EndDateTimeDialog } from "../booking/components/EndDateTimeDialog";
import { UpdateDataBackup } from "../backup/usecases/UpdateDataBackup";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { suggestionBackgroundColor } from "../booking/style/BookingStyle";
import { Formik } from "formik";
import "../booking/style/booking.css";

function getOrderedSubRoutes(booking) {
  const subRoutes = booking?.subRoutes ? Object.values(booking.subRoutes) : [];
  const destination = booking?.destination;
  const orderedSubRoutes = subRoutes
    .filter((subRoute) => subRoute?.name)
    .filter(
      (subRoute) =>
        subRoute.name !== destination?.name ||
        subRoute.lat !== destination?.lat ||
        subRoute.lng !== destination?.lng,
    )
    .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));

  if (destination?.name) {
    orderedSubRoutes.push(destination);
  }

  return orderedSubRoutes;
}

function getDestinationValue(subRoutes) {
  if (!Array.isArray(subRoutes) || subRoutes.length === 0) return "";
  if (subRoutes.length === 1) return subRoutes[0].name || "";

  return `${subRoutes.length} paradas`;
}

function arrayMove(items, fromIndex, toIndex) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);

  return nextItems;
}

function getSortableSubRoutes(subRoutes) {
  return subRoutes.map((subRoute, index) => ({
    ...subRoute,
    sortableId: `quote-hour-sub-route-${subRoute.id || "destination"}-${index}`,
  }));
}

function getSubRoutesFromModalList(subRoutes) {
  return subRoutes.map(({ sortableId, ...subRoute }) => subRoute);
}

function getRouteDestination(route) {
  if (!route) return null;

  const { sortableId, id, ...destination } = route;
  return destination;
}

function getBookingRouteValues(subRoutes) {
  const routes = Array.isArray(subRoutes)
    ? subRoutes
        .map((route) => {
          if (!route) return null;

          const { sortableId, ...subRoute } = route;
          return subRoute;
        })
        .filter((route) => route?.name)
    : [];

  if (routes.length === 0) {
    return {
      destination: null,
      subRoutes: [],
    };
  }

  return {
    destination: getRouteDestination(routes[routes.length - 1]),
    subRoutes: routes.slice(0, -1).map((route, index) => ({
      ...route,
      id: index + 1,
    })),
  };
}

function getNextSubRouteId(subRoutes) {
  const ids = subRoutes.map((subRoute) => Number(subRoute.id || 0));
  return Math.max(0, ...ids) + 1;
}

function SubRouteSortableItem({ subRoute, index, onDelete }) {
  const { ref, handleRef, isDragging } = useSortable({
    id: String(subRoute.sortableId),
    index,
    group: "quote-hour-sub-routes",
  });

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        px: 1,
        py: 0.75,
        border: "1px solid rgba(20, 83, 45, 0.16)",
        borderRadius: 1,
        bgcolor: isDragging ? "rgba(20, 83, 45, 0.08)" : "background.paper",
      }}
    >
      <IconButton ref={handleRef} size="small" sx={{ cursor: "grab" }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {index + 1}. {subRoute.name}
      </Typography>
      <IconButton
        size="small"
        color="error"
        aria-label="eliminar parada"
        onClick={() => onDelete(subRoute.sortableId)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const FormQuoteHour = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { uuid } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);

  const { t } = useTranslation();

  const [note, setNote] = useState("");
  const [noteAdmin, setNoteAdmin] = useState("");
  const [total, setTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [advance, setAdvance] = useState(0);
  const [currency, setCurrency] = useState(1);
  const [persons, setPersons] = useState(0);

  const [dateformat, setDateFormat] = useState("");
  const [timeFormatter, setTimeFormat] = useState("");
  const [endDateTimeDialogOpen, setEndDateTimeDialogOpen] = useState(false);
  const [dateEndFormat, setDateEndFormat] = useState("");
  const [timeEndFormatter, setTimeEndFormat] = useState("");
  const [openSubRoutesModal, setOpenSubRoutesModal] = useState(false);
  const [subRoutesModalList, setSubRoutesModalList] = useState([]);
  const [subRouteSearch, setSubRouteSearch] = useState("");

  useEffect(() => {
    const starCountRef = ref(db, `bookingNow/${uuid}`);
    onValue(starCountRef, (snapshot) => {
      let data = snapshot.val();

      if (data === null) return console.log("No Booking");

      setOrigin(data.origin.name);
      setDestination(data.destination.name);
      setOriginCoords({ lat: data.origin.lat, lng: data.origin.lng });
      setDestCoords({ lat: data.destination.lat, lng: data.destination.lng });
      setORef(data.origin.name);
      setDRef(data.destination.name);
      setNote(data.note);
      setTotal(data.total);
      setAdvance(data.advance ? data.advance : 0);
      setDiscount(data.discount ? data.discount : 0);
      setCurrency(data.currency);
      setBooking({
        ...data,
        subRoutes: getOrderedSubRoutes(data),
      });
      setPersons(data.persons);
      setDateFormat(dayjs(data.date, "DD/MM/YYYY").toDate());
      setTimeFormat(formatterTimePicker(data.date, data.time));
      setDateEndFormat(
        Number(data.booking_type) === 3
          ? dayjs(data.dateEnd, "DD/MM/YYYY").toDate()
          : "",
      );
      setTimeEndFormat(
        Number(data.booking_type) === 3
          ? formatterTimePicker(data.dateEnd, data.timeEnd)
          : "",
      );
      setNoteAdmin(data.noteAdmin);
    });
  }, []);

  const handleOpenEndDateTimeDialog = () => {
    if (!dateEndFormat) setDateEndFormat(dateformat);
    if (!timeEndFormatter) setTimeEndFormat(timeFormatter);
    setEndDateTimeDialogOpen(true);
  };

  const handleAcceptEndDateTimeDialog = () => {
    if (!dateEndFormat || !timeEndFormatter) {
      snackBarDialog("Debe ingresar fecha y hora final", "error");
      return;
    }

    const startDate = getDateOnlyValue(dateformat);
    const endDate = getDateOnlyValue(dateEndFormat);
    const startDateTime = getDateTimeValue(dateformat, timeFormatter);
    const endDateTime = getDateTimeValue(dateEndFormat, timeEndFormatter);

    if (!startDate || !endDate) {
      snackBarDialog("Fecha final invalida", "error");
      return;
    }
    if (endDate < startDate) {
      snackBarDialog(
        "La fecha final debe ser igual o superior a la fecha inicial",
        "error",
      );
      return;
    }
    if (!startDateTime || !endDateTime || endDateTime <= startDateTime) {
      snackBarDialog("La hora final debe ser superior a la hora inicial", "error");
      return;
    }

    setBooking((currentBooking) => ({
      ...currentBooking,
      booking_type: 3,
      dateEnd: dateEndFormat,
      timeEnd: timeEndFormatter,
    }));
    setEndDateTimeDialogOpen(false);
  };

  const handleClearEndDateTimeDialog = () => {
    setDateEndFormat("");
    setTimeEndFormat("");
    setBooking((currentBooking) => ({
      ...currentBooking,
      booking_type:
        currentBooking?.booking_type && Number(currentBooking.booking_type) !== 3
          ? currentBooking.booking_type
          : 2,
      dateEnd: "",
      timeEnd: "",
    }));
    setEndDateTimeDialogOpen(false);
  };

  const handleClickSave = async (value) => {
    try {
      setIsLoading(true);
      const routeValues = getBookingRouteValues(value.subRoutes);

      if (routeValues.destination) {
        value.destination = routeValues.destination;
        value.subRoutes = routeValues.subRoutes;
      }

      value.cotizado = false;
      value.currency = Number(currency);
      value.statusPayment = "ASIGNADO";
      value.publishment = false;
      value.date = value.date.$d ? dateFormat(value.date.$d) : value.date;
      value.time = value.time.$d ? timeFormat(value.time.$d) : value.time;
      value.booking_type = value.booking_type || 2;
      if (Number(value.booking_type) === 3) {
        value.dateEnd = formatDatePickerValue(value.dateEnd);
        value.timeEnd = formatTimePickerValue(value.timeEnd);
      } else {
        value.dateEnd = "";
        value.timeEnd = "";
      }

      value.advance = advance === "" ? 0 : Number(advance);
      value.discount = discount === "" ? 0 : Number(discount);
      value.total = total === "" ? 0 : Number(total);
      value.persons = persons === "" ? 0 : Number(persons);
      const normalizedNote = note ? String(note).toUpperCase() : "";
      const normalizedNoteAdmin = noteAdmin
        ? String(noteAdmin).toUpperCase()
        : "";
      value.note = normalizedNote;
      value.noteAdmin = normalizedNoteAdmin;

      if (origin !== oRef) {
        value.origin = {
          name: origin,
          lat: originCoords.lat ?? value.origin.lat,
          lng: originCoords.lng ?? value.origin.lng,
        };
      }
      if (destination !== dRef) {
        value.destination = {
          name: destination,
          lat: destCoords.lat ?? value.destination.lat,
          lng: destCoords.lng ?? value.destination.lng,
        };
      }

      const openpay = {
        createBy: "A",
        status: "pending",
        btnPay: true,
        currency,
        transaction_id: "",
        order_id: "",
        operation_date: "",
        url: "",
      };

      if (advance > 0) value.openpay = openpay;

      const backup = {
        id: value.counterBooking,
        date: value.date.$d ? dateFormat(value.date.$d) : value.date,
        time: value.time.$d ? timeFormat(value.time.$d) : value.time,
        currency: Number(currency),
        advance: advance === "" ? 0 : Number(advance),
        discount: discount === "" ? 0 : Number(discount),
        total: total === "" ? 0 : Number(total),
        persons: persons === "" ? 0 : Number(persons),
        note: normalizedNote,
        noteAdmin: normalizedNoteAdmin,
        originName: value.origin.name,
        originLat: value.origin.lat,
        originLng: value.origin.lng,
        destinationName: value.destination.name,
        destinationLat: value.destination.lat,
        destinationLng: value.destination.lng,
        subRoutes: value.subRoutes,
        dateEnd: Number(value.booking_type) === 3 ? value.dateEnd : "",
        timeEnd: Number(value.booking_type) === 3 ? value.timeEnd : "",
        bookingType: value.booking_type,
      };

      await update(ref(db, `bookingNow/${uuid}`), value);
      await UpdateDataBackup(backup);
      

      setIsLoading(false);
      snackBarDialog("COTIZACION GUARDADA");
      setTimeout(() => {
        navigate("/admin/list-book-quote");
      }, 5000);
    } catch (error) {
      setIsLoading(false);
      snackBarDialog("ERROR DE COTIZACION GUARDADA", "error");
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };
  const handleCloseSnk = () => setStateSnk(false);

  //editar origen
  const [oRef, setORef] = useState("");
  const [dRef, setDRef] = useState("");

  const [origin, setOrigin] = useState("");
  const [originCoords, setOriginCoords] = useState({ lat: null, lng: null });
  const [destination, setDestination] = useState("");
  const [destCoords, setDestCoords] = useState({ lat: null, lng: null });
  const handleCloseSubRoutesModal = () => setOpenSubRoutesModal(false);
  const handleOpenSubRoutesModal = () => {
    const currentSubRoutes =
      booking.subRoutes?.length > 0
        ? booking.subRoutes
        : [
            {
              name: destination,
              lat: destCoords.lat,
              lng: destCoords.lng,
            },
          ];

    setSubRoutesModalList(getSortableSubRoutes(currentSubRoutes));
    setSubRouteSearch("");
    setOpenSubRoutesModal(true);
  };
  const handleSubRoutesDragEnd = (event) => {
    const operation = event.operation;

    if (!operation || operation.canceled || !isSortableOperation(operation)) {
      return;
    }

    const sourceIndex = operation.source.initialIndex;
    const targetIndex = operation.source.index;
    if (sourceIndex === targetIndex) return;

    setSubRoutesModalList((currentList) =>
      arrayMove(currentList, sourceIndex, targetIndex),
    );
  };
  const handleDeleteSubRoute = (sortableId) => {
    setSubRoutesModalList((currentList) =>
      currentList.filter((subRoute) => subRoute.sortableId !== sortableId),
    );
  };
  const handleAddSubRoute = async (value) => {
    try {
      const result = await geocodeByAddress(value);
      const ll = await getLatLng(result[0]);

      setSubRoutesModalList((currentList) => {
        const id = getNextSubRouteId(currentList);
        const newSubRoute = {
          id,
          name: value,
          lat: ll.lat,
          lng: ll.lng,
          sortableId: `quote-hour-sub-route-${id}-${Date.now()}`,
        };

        return [...currentList, newSubRoute];
      });
      setSubRouteSearch("");
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE AGREGO LA PARADA", "error");
    }
  };
  const handleSaveSubRoutesModal = () => {
    const subRoutes = getSubRoutesFromModalList(subRoutesModalList);
    const routeValues = getBookingRouteValues(subRoutes);

    if (!routeValues.destination) {
      snackBarDialog("Debe seleccionar un destino", "error");
      return;
    }

    setBooking((currentBooking) => ({
      ...currentBooking,
      destination: routeValues.destination,
      subRoutes,
    }));
    setDestination(routeValues.destination.name);
    setDestCoords({
      lat: routeValues.destination.lat,
      lng: routeValues.destination.lng,
    });
    handleCloseSubRoutesModal();
  };
  const handleOriSelect = async (value) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);
    setOrigin(value);
    setOriginCoords(ll);
  };
  const handleDestSelect = async (value) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);
    const destinationValue = {
      name: value,
      lat: ll.lat,
      lng: ll.lng,
    };

    setDestination(value);
    setDestCoords(ll);
    setBooking((currentBooking) => ({
      ...currentBooking,
      destination: destinationValue,
      subRoutes:
        currentBooking.subRoutes?.length > 1
          ? currentBooking.subRoutes
          : [destinationValue],
    }));
  };
  const hasSubRoutes = (booking?.subRoutes || []).length > 1;

  const BoxSuggestion = ({
    loading,
    suggestions,
    getSuggestionItemProps,
    state,
  }) => {
    return (
      <Box
        key={suggestions.description}
        className={`autocomplete-dropdown-container ${
          state ? "suggestions" : "suggestionsDestination"
        }`}
      >
        {loading && <div>Loading...</div>}
        {suggestions.map((suggestion) => {
          const className = suggestion.active
            ? "suggestion-item--active"
            : "suggestion-item";
          const style = suggestion.active
            ? suggestionBackgroundColor(colors.primary[500])
            : suggestionBackgroundColor(colors.primary[300]);
          return (
            <div
              key={suggestion.description}
              {...getSuggestionItemProps(suggestion, {
                className,
                style,
              })}
            >
              <span>{suggestion.description}</span>
            </div>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      {booking && (
        <Box backgroundColor={colors.primary[400]} p={2} m={1} borderRadius={3}>
          <Formik>
            {({ handleChange }) => (
              <form>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <PlacesAutocomplete
                      value={origin}
                      onChange={setOrigin}
                      onSelect={handleOriSelect}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <div key={suggestions.description}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel>Origen</InputLabel>
                            <OutlinedInput
                              id="outlined-adornment-weight"
                              onChange={handleChange("weight")}
                              aria-describedby="outlined-weight-helper-text"
                              label="Origen"
                              {...getInputProps({
                                placeholder: t("origin"),
                                className: "location-search-input",
                              })}
                            />
                          </FormControl>
                          <BoxSuggestion
                            loading={loading}
                            suggestions={suggestions}
                            getSuggestionItemProps={getSuggestionItemProps}
                            state={true}
                          />
                        </div>
                      )}
                    </PlacesAutocomplete>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PlacesAutocomplete
                      value={
                        hasSubRoutes
                          ? getDestinationValue(booking.subRoutes)
                          : destination
                      }
                      onChange={(value) => {
                        if (!hasSubRoutes) setDestination(value);
                      }}
                      onSelect={handleDestSelect}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <div>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel>Destino</InputLabel>
                            <OutlinedInput
                              id="outlined-adornment-weight"
                              onChange={handleChange("weight")}
                              aria-describedby="outlined-weight-helper-text"
                              label="Destino"
                              {...getInputProps({
                                placeholder: t("destination"),
                                className: "location-search-input",
                                readOnly: hasSubRoutes,
                                onClick: hasSubRoutes
                                  ? handleOpenSubRoutesModal
                                  : undefined,
                              })}
                              endAdornment={
                                <InputAdornment position="end">
                                  <IconButton
                                    edge="end"
                                    aria-label="rutas de destino"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                    }}
                                    onClick={handleOpenSubRoutesModal}
                                  >
                                    <LocationOnIcon />
                                  </IconButton>
                                </InputAdornment>
                              }
                              sx={{
                                "& .MuiInputBase-input": {
                                  cursor: hasSubRoutes ? "pointer" : "text",
                                },
                              }}
                            />
                          </FormControl>
                          {!hasSubRoutes && (
                            <BoxSuggestion
                              loading={loading}
                              suggestions={suggestions}
                              getSuggestionItemProps={getSuggestionItemProps}
                              state={false}
                            />
                          )}
                        </div>
                      )}
                    </PlacesAutocomplete>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>

          <Grid container spacing={1} marginTop={1}>

            <Grid item xs={12} sm={6}>
              <TextField
                value={booking.clientName}
                fullWidth
                disabled
                variant="outlined"
                color="success"
                type="text"
                label={t("client")}
                name="client"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={3} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={2}>
                      <MobileDatePicker
                        label={t("date")}
                        inputFormat="DD/MM/YYYY"
                        name="date"
                        value={dateformat}
                        onChange={(newValue) => {
                          setDateFormat(newValue);
                          booking.date = newValue;
                        }}
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </Stack>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={3}>
                      <MobileTimePicker
                        label={t("time")}
                        value={timeFormatter}
                        name="time"
                        onChange={(newValue) => {
                          setTimeFormat(newValue);
                          booking.time = newValue;
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    edge="end"
                                    aria-label="hora final"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleOpenEndDateTimeDialog();
                                    }}
                                  >
                                    <AccessTimeIcon fontSize="small" />
                                  </IconButton>
                                  {params.InputProps.endAdornment}
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Stack>
                  </LocalizationProvider>
                </Grid>
                {Number(booking.booking_type) === 3 && (
                  <>
                    <Grid item xs={6} sm={3} md={3}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileDatePicker
                          label={t("dateEnd")}
                          inputFormat="DD/MM/YYYY"
                          name="dateEnd"
                          value={dateEndFormat}
                          onChange={(newValue) => {
                            setDateEndFormat(newValue);
                            booking.dateEnd = newValue;
                          }}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6} sm={3} md={3}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileTimePicker
                          label={t("timeEnd")}
                          name="timeEnd"
                          value={timeEndFormatter}
                          onChange={(newValue) => {
                            setTimeEndFormat(newValue);
                            booking.timeEnd = newValue;
                          }}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </>
                )}
                <Grid item xs={6} sm={3} md={3}>
                  <TextField
                    value={persons}
                    fullWidth
                    variant="outlined"
                    color="success"
                    type="number"
                    label={t("persons")}
                    name="persons"
                    onChange={(e) => setPersons(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>{t("Model")}</InputLabel>
                    <Select
                      value={booking.vehicle}
                      id="vehicle"
                      name="vehicle"
                      label={t("vehicle")}
                      onChange={(e) => {
                        booking.vehicle = e.target.value;
                      }}
                    >
                      <MenuItem value={1}>{t("Sedan")}</MenuItem>
                      <MenuItem value={2}>{t("SUV")}</MenuItem>
                      <MenuItem value={3}>{t("Minivan")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6} sm={2}>
              <FormControl fullWidth>
                <InputLabel>{t("currency")}</InputLabel>
                <Select
                  value={currency}
                  id="currency"
                  name="currency"
                  label={t("currency")}
                  onChange={(e) => {
                    setCurrency(e.target.value);
                  }}
                >
                  <MenuItem value={1}>{t("PEN")}</MenuItem>
                  <MenuItem value={2}>{t("USD")}</MenuItem>
                  <MenuItem value={3}>{t("EUR")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                value={advance}
                name="advance"
                fullWidth
                variant="outlined"
                color="success"
                type="number"
                label="Adelanto"
                onChange={(e) => {
                  setAdvance(e.target.value);
                }}
                inputProps={{ style: { fontSize: 14 } }}
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                value={discount}
                name="discount"
                fullWidth
                variant="outlined"
                color="success"
                type="number"
                label="Descuento"
                onChange={(e) => {
                  setDiscount(e.target.value);
                }}
                inputProps={{ style: { fontSize: 14 } }}
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                value={total}
                fullWidth
                variant="outlined"
                color="success"
                type="number"
                label={t("total")}
                onChange={(e) => {
                  setTotal(e.target.value);
                }}
                name="total"
                inputProps={{ style: { fontSize: 14 } }}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <TextField
                value={note}
                fullWidth
                name="note"
                variant="outlined"
                color="success"
                type="text"
                label={t("notes")}
                onChange={(e) => {
                  console.log(e.target.value);
                  setNote(e.target.value);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <TextField
                value={noteAdmin}
                fullWidth
                name="note"
                variant="outlined"
                color="success"
                type="text"
                label="Notas solo para Admin"
                onChange={(e) => setNoteAdmin(e.target.value)}
              />
            </Grid>

            <Grid item xs={6} sm={12} textAlign={"end"}>
              <Button
                color="secondary"
                variant="contained"
                onClick={(e) => {
                  handleClickSave(booking);
                }}
                disabled={isLoading}
              >
                {t("saveChanges")}
              </Button>
            </Grid>
          </Grid>
          <SnackBar
            stateSnk={stateSnk}
            vertical={vertical}
            horizontal={horizontal}
            duration={4000}
            handleClose={handleCloseSnk}
            severity={severity}
            messageSnk={messageSnk}
          />
          <EndDateTimeDialog
            open={endDateTimeDialogOpen}
            onClose={() => setEndDateTimeDialogOpen(false)}
            dateEnd={dateEndFormat}
            timeEnd={timeEndFormatter}
            dateLabel={t("dateEnd")}
            timeLabel={t("timeEnd")}
            onDateEndChange={setDateEndFormat}
            onTimeEndChange={setTimeEndFormat}
            onClear={handleClearEndDateTimeDialog}
            onAccept={handleAcceptEndDateTimeDialog}
            acceptLabel={t("saveChanges")}
          />
          <Dialog
            open={openSubRoutesModal}
            onClose={handleSaveSubRoutesModal}
            fullWidth
            maxWidth="xs"
            PaperProps={{
              sx: {
                height: "80vh",
                maxHeight: "90vh",
              },
            }}
          >
            <DialogTitle>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ alignItems: { xs: "stretch", sm: "center" } }}
              >
                <Typography variant="h6" sx={{ flexShrink: 0 }}>
                  Paradas
                </Typography>
                <PlacesAutocomplete
                  value={subRouteSearch}
                  onChange={setSubRouteSearch}
                  onSelect={handleAddSubRoute}
                  searchOptions={{
                    componentRestrictions: { country: "pe" },
                  }}
                >
                  {({
                    getInputProps,
                    suggestions,
                    getSuggestionItemProps,
                    loading,
                  }) => (
                    <Box sx={{ flex: 1, position: "relative" }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t("Destino")}
                        {...getInputProps({
                          className: "location-search-input",
                        })}
                      />
                      <Box
                        className="autocomplete-dropdown-container suggestionsDestination"
                        sx={{ width: "100%" }}
                      >
                        {loading && <div>Loading...</div>}
                        {suggestions.map((suggestion) => {
                          const className = suggestion.active
                            ? "suggestion-item--active"
                            : "suggestion-item";
                          const style = suggestion.active
                            ? suggestionBackgroundColor(colors.primary[500])
                            : suggestionBackgroundColor(colors.primary[300]);

                          return (
                            <div
                              key={suggestion.description}
                              {...getSuggestionItemProps(suggestion, {
                                className,
                                style,
                              })}
                            >
                              <span>{suggestion.description}</span>
                            </div>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </PlacesAutocomplete>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={1} sx={{ pt: 1 }}>
                <DragDropProvider onDragEnd={handleSubRoutesDragEnd}>
                  <Stack spacing={1}>
                    {subRoutesModalList.map((subRoute, index) => (
                      <SubRouteSortableItem
                        key={subRoute.sortableId}
                        subRoute={subRoute}
                        index={index}
                        onDelete={handleDeleteSubRoute}
                      />
                    ))}
                  </Stack>
                </DragDropProvider>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                type="button"
                variant="contained"
                color="error"
                onClick={handleSaveSubRoutesModal}
              >
                {t("Cerrar")}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </>
  );
};

export default FormQuoteHour;
